import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanknotesIcon,
  EyeIcon,
  XMarkIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import {
  adminGetRefunds,
  adminApproveRefund,
  adminRejectRefund,
  adminCompleteRefund,
  RefundWithApplication,
  getRefundStatusLabel,
  getRefundStatusColor,
} from '../../services/refundService';

const RefundManagement: React.FC = () => {
  const [refunds, setRefunds] = useState<RefundWithApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'requested' | 'approved' | 'completed' | 'cancelled'>('all');

  // 모달 상태
  const [selectedRefund, setSelectedRefund] = useState<RefundWithApplication | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // 처리 중 상태
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadRefunds();
  }, [statusFilter]);

  const loadRefunds = async () => {
    setLoading(true);
    try {
      const response = await adminGetRefunds(statusFilter === 'all' ? undefined : statusFilter);
      if (response.success && response.data) {
        setRefunds(response.data);
      } else {
        console.error('Failed to load refunds:', response.error);
      }
    } catch (error) {
      console.error('Error loading refunds:', error);
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 환불 목록
  const filteredRefunds = refunds.filter((refund) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      refund.application?.applicant_name?.toLowerCase().includes(searchLower) ||
      refund.application?.application_number?.toLowerCase().includes(searchLower) ||
      refund.program_info?.name?.toLowerCase().includes(searchLower) ||
      refund.user_info?.phone?.includes(searchTerm)
    );
  });

  // 환불 승인
  const handleApprove = async (refundId: string) => {
    if (!window.confirm('환불을 승인하시겠습니까?')) return;

    setProcessingId(refundId);
    try {
      const response = await adminApproveRefund(refundId);
      if (response.success) {
        alert('환불이 승인되었습니다.');
        await loadRefunds();
      } else {
        alert(response.error?.message || '승인 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Approve error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 환불 거절 모달 열기
  const openRejectModal = (refund: RefundWithApplication) => {
    setSelectedRefund(refund);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // 환불 거절 처리
  const handleReject = async () => {
    if (!selectedRefund) return;
    if (!rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }

    setProcessingId(selectedRefund.id);
    try {
      const response = await adminRejectRefund(selectedRefund.id, rejectionReason);
      if (response.success) {
        alert('환불이 거절되었습니다.');
        setShowRejectModal(false);
        await loadRefunds();
      } else {
        alert(response.error?.message || '거절 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Reject error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 환불 완료 처리
  const handleComplete = async (refundId: string) => {
    if (!window.confirm('환불 완료 처리를 하시겠습니까?\n\n수강 신청이 자동으로 취소되고 대기자가 승급될 수 있습니다.')) return;

    setProcessingId(refundId);
    try {
      const response = await adminCompleteRefund(refundId);
      if (response.success) {
        alert('환불이 완료 처리되었습니다.');
        await loadRefunds();
      } else {
        alert(response.error?.message || '완료 처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Complete error:', error);
      alert('네트워크 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  // 상세 모달 열기
  const openDetailModal = (refund: RefundWithApplication) => {
    setSelectedRefund(refund);
    setShowDetailModal(true);
  };

  // 통계
  const stats = {
    requested: refunds.filter((r) => r.refund_status === 'requested').length,
    approved: refunds.filter((r) => r.refund_status === 'approved').length,
    completed: refunds.filter((r) => r.refund_status === 'completed').length,
    // 총 환불액은 completed 상태만 합산 (취소/거절 제외)
    total: refunds
      .filter((r) => r.refund_status === 'completed')
      .reduce((sum, r) => sum + (r.refund_amount || 0), 0),
  };

  // 상태 라벨 헬퍼 (사용자 취소 vs 관리자 거절 구분)
  const getDisplayStatusLabel = (refund: RefundWithApplication) => {
    if (refund.refund_status === 'cancelled') {
      // rejection_reason이 있으면 관리자 거절, 없으면 사용자 취소
      return refund.rejection_reason ? '환불 거절' : '사용자 취소';
    }
    return getRefundStatusLabel(refund.refund_status);
  };

  const getDisplayStatusColor = (refund: RefundWithApplication) => {
    if (refund.refund_status === 'cancelled') {
      return refund.rejection_reason
        ? 'bg-red-100 text-red-800'  // 관리자 거절
        : 'bg-gray-100 text-gray-600';  // 사용자 취소
    }
    return getRefundStatusColor(refund.refund_status);
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">환불 관리</h1>
          <p className="text-slate-600 mt-1">수강생 환불 신청을 관리합니다.</p>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">대기 중</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.requested}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-full">
                <BanknotesIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">처리 중</p>
                <p className="text-2xl font-bold text-blue-600">{stats.approved}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">완료</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-full">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">총 환불액</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total.toLocaleString()}원</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-full">
                <BanknotesIcon className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </div>
        </div>

        {/* 필터 및 검색 */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-slate-200">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 검색 */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="이름, 신청번호, 프로그램명, 연락처 검색"
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* 상태 필터 */}
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">전체</option>
                <option value="requested">대기 중</option>
                <option value="approved">처리 중</option>
                <option value="completed">완료</option>
                <option value="cancelled">취소</option>
              </select>
            </div>
          </div>
        </div>

        {/* 환불 목록 */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-500 border-r-transparent"></div>
              <p className="mt-4 text-slate-500">불러오는 중...</p>
            </div>
          ) : filteredRefunds.length === 0 ? (
            <div className="text-center py-12">
              <BanknotesIcon className="mx-auto h-12 w-12 text-slate-400" />
              <p className="mt-4 text-slate-500">환불 신청이 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      신청 정보
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      수강생
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      환불 금액
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      신청일
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredRefunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {refund.program_info?.name || '프로그램 정보 없음'}
                          </p>
                          {refund.class_info?.name && refund.class_info.name !== '기본반' && (
                            <p className="text-sm text-slate-500">{refund.class_info.name}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {refund.application?.application_number}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">
                            {refund.application?.applicant_name || refund.user_info?.name}
                          </p>
                          <p className="text-sm text-slate-500">
                            {refund.application?.applicant_phone || refund.user_info?.phone}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-slate-900">
                          {refund.refund_amount?.toLocaleString()}원
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDisplayStatusColor(refund)}`}
                        >
                          {getDisplayStatusLabel(refund)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        {new Date(refund.requested_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {/* 상세 보기 */}
                          <button
                            onClick={() => openDetailModal(refund)}
                            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                            title="상세 보기"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>

                          {/* 승인 버튼 (requested 상태에서만) */}
                          {refund.refund_status === 'requested' && (
                            <>
                              <button
                                onClick={() => handleApprove(refund.id)}
                                disabled={processingId === refund.id}
                                className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg disabled:opacity-50"
                                title="승인"
                              >
                                <CheckCircleIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openRejectModal(refund)}
                                disabled={processingId === refund.id}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                                title="거절"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}

                          {/* 완료 버튼 (approved 상태에서만) */}
                          {refund.refund_status === 'approved' && (
                            <button
                              onClick={() => handleComplete(refund.id)}
                              disabled={processingId === refund.id}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50"
                            >
                              환불 완료
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 상세 모달 — 수강료 환불 신청서 양식 + 인쇄 */}
      {showDetailModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 (인쇄 시 숨김) */}
            <div className="flex items-center justify-between p-4 border-b print:hidden">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-slate-900">환불 신청서 상세</h3>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getRefundStatusColor(selectedRefund.refund_status)}`}>
                  {getRefundStatusLabel(selectedRefund.refund_status)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  <PrinterIcon className="h-4 w-4" />
                  인쇄
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-slate-400 hover:text-slate-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* 환불 신청서 양식 (인쇄 대상) */}
            <div className="p-6 print:p-4 print-form print-a4-fit">
              {/* 로고 + 제목 */}
              <div className="flex items-center mb-4">
                <img src="/logo-full.png" alt="동두천시 글로벌인재교육센터" className="h-6" />
              </div>
              <div className="text-center mb-4">
                <h2 className="text-lg font-bold text-gray-900">수강료 환불 신청서</h2>
              </div>

              {/* 전체를 하나의 테이블로 구성 (A4 맞춤) */}
              <table className="w-full border-collapse border border-gray-400 mb-3 text-xs">
                <tbody>
                  {/* 안내문 */}
                  <tr>
                    <td colSpan={5} className="border border-gray-400 px-2 py-1.5 text-center text-[10px] text-gray-500">
                      &lt;서류 미 제출시 환불이 불가합니다.&gt;
                    </td>
                  </tr>
                  {/* 인적사항 */}
                  <tr>
                    <td rowSpan={4} className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium w-20 text-center">인적사항</td>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">수강과목</td>
                    <td colSpan={3} className="border border-gray-400 px-2 py-1.5">{selectedRefund.program_info?.name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">수강생 성명</td>
                    <td className="border border-gray-400 px-2 py-1.5">{selectedRefund.application?.applicant_name || selectedRefund.user_info?.name || '-'}</td>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">수강생 학년</td>
                    <td className="border border-gray-400 px-2 py-1.5">{selectedRefund.user_info?.grade || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">생년월일</td>
                    <td className="border border-gray-400 px-2 py-1.5">{selectedRefund.user_info?.birthdate || '-'}</td>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">보호자 성명</td>
                    <td className="border border-gray-400 px-2 py-1.5">{selectedRefund.user_info?.guardian_name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">수강료 환불계좌</td>
                    <td className="border border-gray-400 px-2 py-1.5">
                      {selectedRefund.refund_bank ? `${selectedRefund.refund_bank} ${selectedRefund.refund_account || ''}` : '-'}
                    </td>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium whitespace-nowrap">보호자 휴대전화</td>
                    <td className="border border-gray-400 px-2 py-1.5">{selectedRefund.user_info?.guardian_phone || '-'}</td>
                  </tr>
                  {/* 개인정보 수집 동의 */}
                  <tr>
                    <td className="border border-gray-400 px-2 py-2 bg-gray-50 font-medium w-20 text-center">개인정보<br/>수집 및<br/>이용 동의</td>
                    <td colSpan={4} className="border border-gray-400 px-2 py-1.5">
                      <p className="text-[10px] text-gray-600 mb-1">동두천시글로벌인재교육센터가 수집한 개인정보를 환불 업무의 목적으로 보유하고 이용하는데 동의하십니까?</p>
                      <div className="text-center my-1">동의 {selectedRefund.privacy_consent === true ? '☑' : '☐'} &nbsp;&nbsp;&nbsp; 동의안함 {selectedRefund.privacy_consent === false ? '☑' : (selectedRefund.privacy_consent === null ? '-' : '☐')}</div>
                      <p className="text-[10px] text-gray-400">※ 미동의시 수강료 환불 신청이 불가합니다.</p>
                    </td>
                  </tr>
                  {/* 환불 신청사유 — A4 채움 */}
                  <tr>
                    <td className="border border-gray-400 px-2 py-1.5 bg-gray-50 font-medium w-20 text-center align-top">환불<br/>신청사유</td>
                    <td colSpan={4} className="border border-gray-400 px-2 py-1.5 whitespace-pre-wrap" style={{ height: '350px', verticalAlign: 'top' }}>{selectedRefund.refund_reason || '-'}</td>
                  </tr>
                  {/* 서명란 (테이블 내부) */}
                  <tr>
                    <td colSpan={5} className="border border-gray-400 px-4 py-4 text-center text-xs">
                      <div className="mb-3">본인은 위와 같은 사유로 수강료 환불을 신청합니다.</div>
                      <div className="mb-3">
                        {(() => {
                          const d = new Date(selectedRefund.requested_at);
                          return `20${String(d.getFullYear()).slice(2)} 년  ${d.getMonth() + 1} 월  ${d.getDate()} 일     작 성 자(보호자): ${selectedRefund.application?.applicant_name || selectedRefund.user_info?.name || '-'}          (인)`;
                        })()}
                      </div>
                      <div className="text-sm font-semibold text-gray-800">동두천시글로벌인재교육센터장 귀하</div>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* 처리 이력 (인쇄 시 숨김) */}
              {(selectedRefund.approved_at || selectedRefund.completed_at || selectedRefund.rejection_reason) && (
                <div className="mt-4 pt-3 border-t border-gray-300 print:hidden">
                  <h4 className="text-xs font-semibold text-gray-700 mb-1">처리 이력</h4>
                  <div className="space-y-0.5 text-xs text-gray-600">
                    <p>신청일: {new Date(selectedRefund.requested_at).toLocaleString('ko-KR')}</p>
                    {selectedRefund.approved_at && <p>승인일: {new Date(selectedRefund.approved_at).toLocaleString('ko-KR')}</p>}
                    {selectedRefund.completed_at && <p>완료일: {new Date(selectedRefund.completed_at).toLocaleString('ko-KR')}</p>}
                    {selectedRefund.rejection_reason && <p className="text-red-600">거절 사유: {selectedRefund.rejection_reason}</p>}
                    {selectedRefund.admin_note && <p>관리자 메모: {selectedRefund.admin_note}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* 모달 하단 (인쇄 시 숨김) */}
            <div className="flex justify-between items-center p-4 border-t bg-slate-50 print:hidden">
              <div className="text-sm text-slate-500">
                환불 금액: <span className="font-semibold text-slate-900">{selectedRefund.refund_amount?.toLocaleString()}원</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  닫기
                </button>
                {selectedRefund.refund_status === 'requested' && (
                  <>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        openRejectModal(selectedRefund);
                      }}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                    >
                      거절
                    </button>
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        handleApprove(selectedRefund.id);
                      }}
                      disabled={processingId === selectedRefund.id}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      승인
                    </button>
                  </>
                )}
                {selectedRefund.refund_status === 'approved' && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleComplete(selectedRefund.id);
                    }}
                    disabled={processingId === selectedRefund.id}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    환불 완료 처리
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 거절 모달 */}
      {showRejectModal && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-slate-900">환불 거절</h3>
              <button
                onClick={() => setShowRejectModal(false)}
                className="text-slate-400 hover:text-slate-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-900">
                  {selectedRefund.program_info?.name}
                </p>
                <p className="text-sm text-slate-500">
                  {selectedRefund.application?.applicant_name} /{' '}
                  {selectedRefund.refund_amount?.toLocaleString()}원
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  거절 사유 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  placeholder="환불 거절 사유를 입력해주세요"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-slate-50">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processingId === selectedRefund.id}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={processingId === selectedRefund.id || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingId === selectedRefund.id ? '처리 중...' : '거절'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundManagement;
